import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ruler, ArrowRight, Sparkle, Tag, FileMagnifyingGlass, House } from "@phosphor-icons/react";

const TIERS = [
    { id: "t1", label: "< 170 m²", min: 0, max: 170, price: 500, type: "fixed" },
    { id: "t2", label: "171 — 200 m²", min: 171, max: 200, price: 750, type: "fixed" },
    { id: "t3", label: "201 — 250 m²", min: 201, max: 250, price: 950, type: "fixed" },
    { id: "t4", label: "> 250 m²", min: 251, max: 600, price: null, type: "quote" },
];

function getTier(area) {
    return TIERS.find((t) => area >= t.min && area <= t.max) || TIERS[TIERS.length - 1];
}

export default function PriceCalculator() {
    const [area, setArea] = useState(120);

    const tier = useMemo(() => getTier(area), [area]);
    const isQuote = tier.type === "quote";

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="price-calculator"
            className="relative max-w-5xl mx-auto mb-14 rounded-3xl bg-white border border-lumos-border shadow-[0_30px_80px_-30px_rgba(10,17,40,0.18)] overflow-hidden"
        >
            {/* Gold accent line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-lumos-amber to-transparent" />

            <div className="grid lg:grid-cols-12">
                {/* LEFT — INPUTS */}
                <div className="lg:col-span-7 p-7 lg:p-10">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="w-7 h-7 rounded-full bg-lumos-amber/15 text-lumos-amber grid place-items-center">
                            <Sparkle size={14} weight="fill" />
                        </span>
                        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-lumos-amber">
                            Estimation instantanée
                        </span>
                    </div>

                    <h3 className="font-display font-extrabold text-2xl lg:text-[32px] leading-[1.1] text-lumos-ink">
                        Estimez votre audit énergétique{" "}
                        <span className="font-serif-display text-lumos-blue">en 5 secondes.</span>
                    </h3>
                    <p className="mt-3 text-sm text-lumos-muted">
                        Indiquez la surface de votre maison — obtenez votre tarif immédiatement.
                    </p>

                    {/* SURFACE INPUT */}
                    <div className="mt-7">
                        <div className="flex items-center justify-between mb-2.5">
                            <label htmlFor="area-input" className="text-sm font-semibold text-lumos-ink flex items-center gap-2">
                                <Ruler size={16} weight="bold" className="text-lumos-blue" />
                                Surface de la maison
                            </label>
                            <div className="text-sm font-semibold text-lumos-muted">
                                <span className="font-display text-lumos-ink font-extrabold text-xl">{area}</span>{" "}
                                <span className="text-sm">m²</span>
                            </div>
                        </div>

                        <input
                            id="area-input"
                            type="range"
                            min="40"
                            max="400"
                            step="5"
                            value={area}
                            onChange={(e) => setArea(Number(e.target.value))}
                            data-testid="calculator-slider"
                            aria-label="Surface en mètres carrés"
                            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-lumos-border accent-lumos-blue"
                            style={{
                                background: `linear-gradient(90deg, #00AFFF 0%, #00AFFF ${
                                    ((area - 40) / (400 - 40)) * 100
                                }%, #E5E7EB ${((area - 40) / (400 - 40)) * 100}%, #E5E7EB 100%)`,
                            }}
                        />

                        <div className="flex justify-between mt-2 text-[11px] text-lumos-muted/70 font-medium">
                            <span>40 m²</span>
                            <span>200 m²</span>
                            <span>400 m²</span>
                        </div>
                    </div>

                    {/* TIER PILLS */}
                    <div className="mt-7">
                        <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-lumos-muted mb-3">
                            Ou choisissez directement
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {TIERS.map((t) => {
                                const active = tier.id === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setArea(t.type === "quote" ? 300 : t.min === 0 ? 120 : Math.round((t.min + t.max) / 2))}
                                        data-testid={`calculator-tier-${t.id}`}
                                        className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all duration-300 ${
                                            active
                                                ? "bg-lumos-midnight text-white border-lumos-midnight shadow-[0_8px_22px_-8px_rgba(10,17,40,0.45)]"
                                                : "bg-white text-lumos-ink border-lumos-border hover:border-lumos-blue hover:text-lumos-blue"
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* RIGHT — RESULT */}
                <div className="lg:col-span-5 relative bg-gradient-to-br from-lumos-midnight to-[#101b3a] text-white p-7 lg:p-10 flex flex-col">
                    <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                    <div className="absolute -top-24 -right-20 w-56 h-56 rounded-full bg-lumos-blue/25 blur-3xl pointer-events-none" />

                    <div className="relative flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 rounded-full bg-lumos-green animate-pulse" />
                        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/70">
                            Votre estimation
                        </span>
                    </div>

                    <div className="relative flex items-center gap-2 text-white/80 text-sm mb-5">
                        <House size={16} weight="bold" />
                        {tier.label}
                    </div>

                    <AnimatePresence mode="wait">
                        {!isQuote ? (
                            <motion.div
                                key={tier.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.35 }}
                                className="relative"
                            >
                                <div className="flex items-end gap-1.5">
                                    <span className="font-display text-7xl lg:text-[88px] font-extrabold tracking-tight leading-[0.95]">
                                        {tier.price}
                                    </span>
                                    <span className="font-display text-4xl font-bold text-lumos-amber mb-2">€</span>
                                    <span className="text-sm font-semibold text-white/65 mb-3 ml-1">TTC</span>
                                </div>
                                <div className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lumos-amber/15 border border-lumos-amber/40 text-lumos-amber text-[10px] font-bold tracking-[0.15em] uppercase">
                                    <Tag size={11} weight="fill" /> Prix fixe — tout compris
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="quote"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.35 }}
                                className="relative"
                            >
                                <div className="font-display text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
                                    Sur devis
                                </div>
                                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lumos-green/15 border border-lumos-green/40 text-lumos-green text-[10px] font-bold tracking-[0.15em] uppercase">
                                    <FileMagnifyingGlass size={11} weight="fill" /> Étude personnalisée
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <ul className="relative mt-7 space-y-1.5 text-xs text-white/65">
                        <li>· Visite sur site complète</li>
                        <li>· Rapport sous 24-48h</li>
                        <li>· Recommandations de travaux</li>
                    </ul>

                    <a
                        href="#contact"
                        data-testid="calculator-cta"
                        className="relative mt-auto pt-7 inline-flex items-center justify-between gap-2 group"
                    >
                        <span className="btn-primary !w-full justify-center group-hover:translate-y-[-2px]">
                            {isQuote ? "Demander un devis" : "Réserver mon audit"}
                            <ArrowRight size={18} weight="bold" />
                        </span>
                    </a>
                </div>
            </div>
        </motion.div>
    );
}
